import { twJoin } from 'tailwind-merge';

import Image from 'molecules/Image';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import Reveal from 'components/Animations/Reveal';

import { hasArrayValues } from 'utils/array/arrays';

import type { ConnectHeroConfigProps } from 'components/ConnectHero/config';
import type { FC } from 'react';

const ConnectHero: FC<ConnectHeroConfigProps> = props => {
  const {
    sectionProps,
    rest: { eyebrow, heading, body, bullets, image, reverse },
  } = splitSectionProps(props);

  return (
    <Section {...sectionProps}>
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
        {/* Image (the full composition is a single uploaded image). On mobile it
            sits below the text/bullets; on desktop `reverse` swaps the sides. */}
        {image?.file?.url && (
          <Reveal className={twJoin('order-2', reverse ? 'lg:order-2' : 'lg:order-1')}>
            <Image
              url={image.file.url}
              alt={image.alt || ''}
              noFill
              unsetMaxW
              unsetRatio
              width={image.file.width}
              height={image.file.height}
              className="h-auto w-full"
            />
          </Reveal>
        )}

        {/* Text column */}
        <Reveal
          delay={0.1}
          className={twJoin('order-1 flex flex-col items-start gap-4', reverse ? 'lg:order-1' : 'lg:order-2')}
        >
          {eyebrow && <span className="font-robotoMono text-md font-medium text-primary-300">{eyebrow}</span>}
          {heading && (
            <h2 className="font-spaceGrotesk text-display-sm font-semibold text-white sm:text-display-lg">{heading}</h2>
          )}
          {body && <p className="text-lg font-medium leading-7 text-gray-300">{body}</p>}

          {hasArrayValues(bullets) && (
            <ul className="mt-2 flex flex-col gap-5">
              {bullets.map((bullet, index) => (
                <li
                  key={bullet.id ?? `bullet-${index}`}
                  className="border-l-2 border-primary-300 pl-3 text-lg leading-snug text-gray-300"
                >
                  <span className="font-semibold text-white">
                    {bullet.text}
                    {bullet.description ? ':' : ''}
                  </span>
                  {bullet.description ? ` ${bullet.description}` : ''}
                </li>
              ))}
            </ul>
          )}
        </Reveal>
      </div>
    </Section>
  );
};

export default ConnectHero;
